import React, { useState, useEffect, useContext } from "react";
import { Plus, Trash2, Pencil, Check, X, RefreshCw } from "lucide-react";
import { toast } from "react-hot-toast";
import { AuthContext } from "../context/AuthContext";
import {
  getAllTrademarks,
  createTrademark,
  updateTrademark,
  deleteTrademark,
} from "../api/trademarkApi";

export default function TrademarkPrefixManagementSection() {
  const { canWrite } = useContext(AuthContext);
  const canManage = canWrite("CostingSettingsPage") || canWrite("AccessCosting");

  const [trademarks, setTrademarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newBrand, setNewBrand] = useState("");
  const [newPrefix, setNewPrefix] = useState("");
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editingBrand, setEditingBrand] = useState("");
  const [editingPrefix, setEditingPrefix] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadTrademarks();
  }, []);

  const loadTrademarks = async () => {
    setLoading(true);
    try {
      const data = await getAllTrademarks();
      setTrademarks(data || []);
    } catch (err) {
      toast.error("Failed to load trademarks");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newBrand.trim()) {
      toast.error("Brand name is required");
      return;
    }

    if (newPrefix.trim() && !/^\d{4}$/.test(newPrefix.trim())) {
      toast.error("Prefix must be a 4-digit number (e.g. 1538)");
      return;
    }

    setAdding(true);
    try {
      await createTrademark({
        name: newBrand.trim(),
        customPrefix: newPrefix.trim() || undefined,
      });
      toast.success("Trademark created successfully");
      setNewBrand("");
      setNewPrefix("");
      await loadTrademarks();
    } catch (err) {
      toast.error(err.message || "Failed to create trademark");
    } finally {
      setAdding(false);
    }
  };

  const handleStartEdit = (tm) => {
    setEditingId(tm._id || tm.id);
    setEditingBrand(tm.name || "");
    setEditingPrefix(tm.customPrefix || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingBrand("");
    setEditingPrefix("");
  };

  const handleSaveEdit = async (id) => {
    if (!editingBrand.trim()) {
      toast.error("Brand name cannot be empty");
      return;
    }

    if (editingPrefix.trim() && !/^\d{4}$/.test(editingPrefix.trim())) {
      toast.error("Prefix must be a 4-digit number (e.g. 1538)");
      return;
    }

    setUpdating(true);
    try {
      await updateTrademark(id, {
        name: editingBrand.trim(),
        customPrefix: editingPrefix.trim() || null,
      });
      toast.success("Trademark updated successfully");
      setEditingId(null);
      setEditingBrand("");
      setEditingPrefix("");
      await loadTrademarks();
    } catch (err) {
      toast.error(err.message || "Failed to update trademark");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to remove trademark "${name}"?`)) return;
    try {
      await deleteTrademark(id);
      toast.success("Trademark removed");
      await loadTrademarks();
    } catch (err) {
      toast.error(err.message || "Failed to delete trademark");
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">
            Trademarks & Customer SKU Prefixes
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Manage custom private label trademarks and allocated 4-digit SKU prefixes (e.g. 1535, 1536, 1537).
          </p>
        </div>
        <button
          type="button"
          onClick={loadTrademarks}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Add New Trademark & Prefix */}
      {canManage && (
        <form onSubmit={handleAdd} className="mb-6 bg-gray-50 p-4 rounded-xl border border-gray-200">
          <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
            Add New Trademark / Private Label Client
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              placeholder="Brand / Customer Name (e.g. ROB'S FAMOUS)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
            />
            <input
              type="text"
              maxLength={4}
              value={newPrefix}
              onChange={(e) => setNewPrefix(e.target.value.replace(/\D/g, ""))}
              placeholder="4-Digit Prefix (e.g. 1538)"
              className="w-full sm:w-44 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-black"
            />
            <button
              type="submit"
              disabled={adding}
              className="flex items-center justify-center gap-1.5 px-5 py-2 bg-black hover:bg-gray-800 text-white rounded-lg text-sm font-semibold transition disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> {adding ? "Adding..." : "Add Trademark"}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            Prefix must be 4 digits. Leave blank to auto-allocate upon SKU generation.
          </p>
        </form>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-100 text-xs font-semibold text-gray-700 uppercase">
            <tr>
              <th className="px-5 py-3">Trademark / Brand Name</th>
              <th className="px-5 py-3">Allocated 4-Digit SKU Prefix</th>
              <th className="px-5 py-3 text-center">Status</th>
              {canManage && <th className="px-5 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {trademarks.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 4 : 3} className="px-5 py-6 text-center text-gray-400 italic">
                  {loading ? "Loading trademarks..." : "No trademarks found."}
                </td>
              </tr>
            ) : (
              trademarks.map((tm) => {
                const tmId = tm._id || tm.id;
                const isEditing = editingId === tmId;

                return (
                  <tr key={tmId} className="hover:bg-gray-50/70 transition">
                    <td className="px-5 py-3 font-medium text-gray-900">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingBrand}
                          onChange={(e) => setEditingBrand(e.target.value)}
                          className="w-full px-2.5 py-1 border border-gray-300 rounded text-sm bg-white"
                          autoFocus
                        />
                      ) : (
                        <span>{tm.name}</span>
                      )}
                    </td>

                    <td className="px-5 py-3">
                      {isEditing ? (
                        <input
                          type="text"
                          maxLength={4}
                          value={editingPrefix}
                          onChange={(e) => setEditingPrefix(e.target.value.replace(/\D/g, ""))}
                          placeholder="Prefix"
                          className="w-28 px-2.5 py-1 border border-gray-300 rounded text-sm font-mono bg-white"
                        />
                      ) : tm.customPrefix ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-mono text-xs font-medium">
                          {tm.customPrefix}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs italic">Auto on generate</span>
                      )}
                    </td>

                    <td className="px-5 py-3 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Active
                      </span>
                    </td>

                    {canManage && (
                      <td className="px-5 py-3 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => handleSaveEdit(tmId)}
                              disabled={updating}
                              className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded transition"
                              title="Save"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              className="p-1.5 text-gray-400 hover:bg-gray-100 rounded transition"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleStartEdit(tm)}
                              className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {tm.name !== "A Spice Affair" && (
                              <button
                                type="button"
                                onClick={() => handleDelete(tmId, tm.name)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
